import { Formik, FormikErrors, FormikHelpers } from 'formik';
import { extractServerFormValues, useAlert } from '@uplift/ui-v2';
import { ClaimCorrectionForm } from './claim-correction.form';
import React, { useCallback, useMemo } from 'react';
import { useCurrentuser } from '@uplift/contexts';
import { useAllDiagnosisQuery } from '@uplift/queries';
import { useClaimCorrectionNote } from './hooks/use-claim-correction.hook';
import { updateClaimCorrectionNote } from '@uplift/api';
import { useQueryClient } from '@tanstack/react-query';
import { useHistory } from 'react-router';
import { providerPatientRoute } from '@uplift/constants';
import { ClaimCorrectionFormType } from '@uplift/types';
import { patchClaimCorrection } from './hooks/patch-claim-correction.hook';
import { isAxiosError } from 'axios';
import { runValidate } from './claim-correction.validation';

export type ClaimCorrectionNoteContentProps = {
  noteId: string;
  handleOpen: () => void;
  patientId: string;
};

const enum ResponseError {
  NO_SERVICE_ON_DATE = 'no_service_on_date',
  VALIDATION_ERROR = 'validation_error',
  SUBMITTED_ARCHIVED = 'submitted_archived'
}

export const ClaimCorrectionNoteContent: React.VFC<ClaimCorrectionNoteContentProps> = ({ noteId, handleOpen, patientId }) => {
  const currentUser = useCurrentuser();
  const snackbar = useAlert();
  const history = useHistory();
  const queryClient = useQueryClient();
  const diagnosisData = useAllDiagnosisQuery({ suspense: true });
  const noteData = useClaimCorrectionNote(noteId, { suspense: true });

  const handleSubmit = useCallback(async (values: ClaimCorrectionFormType, helpers: FormikHelpers<ClaimCorrectionFormType>) => {
    const cptCodes: string[] = [];

    if (values.cptCodes && values.cptCodes.length > 0) {
      cptCodes.push(values.cptCodes);
    }

    if (values.addonCptCodes && values.addonCptCodes.length > 0) {
      cptCodes.push(...values.addonCptCodes);
    }
    const noDiagnosis = values.diagnosis.length === 0;
    const noCptCodes = cptCodes.length === 0;
    const noOtherDescription = !values.claim.code_change_description;

    const response = await patchClaimCorrection(noteId, values, cptCodes);

    if ('error' in response) {
      if (response.error === ResponseError.SUBMITTED_ARCHIVED) {
        snackbar.error(response.message);
        return;
      }

      snackbar.error('Unable to save note');

      if (response.error === ResponseError.NO_SERVICE_ON_DATE) {
        helpers.setFieldError('claim.service_date', response.message);
        return;
      }
      if (response.error === ResponseError.VALIDATION_ERROR) {
        queryClient.invalidateQueries({ queryKey: [ 'medical-notes', noteId ], refetchType: 'none' });

        const yupErrors = await runValidate(values);

        const errors: FormikErrors<ClaimCorrectionFormType> = {
          claim: response.form.errors.reduce(
            (acc, cur) => ({
              ...acc,
              [cur.id]: cur.message
            }), {}),
          ...yupErrors
        };

        helpers.setErrors(errors);

        return;
      }
    }

    if (noDiagnosis && noCptCodes && noOtherDescription) {
      snackbar.error('At least one code change required');
      return;
    }

    try {
      const submitResponse = await updateClaimCorrectionNote(noteId, {
        claimCorrectionNoteForm: values.claim,
        diagnosisIds: values.diagnosis,
        cptCodeIds: cptCodes.length > 0 ? cptCodes : undefined,
        isSubmitted: true
      });

      if ('error' in submitResponse) {
        snackbar.error('Unable to submit note');
        return;
      }
    } catch (err) {
      if (isAxiosError(err) && err.response?.data?.error) {
        snackbar.error(err.response.data.error);
        return;
      }

      snackbar.error('Error saving note');

      return;
    }


    history.push(providerPatientRoute.createLink({ patientId }));
  }, [ history, noteId, patientId, queryClient, snackbar ]);

  const initialValues: ClaimCorrectionFormType = useMemo(() => {
    const note = noteData?.data;

    return {
      claim: {
        ...extractServerFormValues(note?.form.fields),
      },
      diagnosis: note.diagnosisIds,
      cptCodes: note.cptCodeIds?.length > 0 ? note.cptCodeIds[0] : null,
      addonCptCodes: []
    };
  }, [ noteData?.data ]);

  const note = noteData?.data;

  return (
    <Formik
      onSubmit={handleSubmit}
      enableReinitialize
      initialValues={initialValues}
    >
      <ClaimCorrectionForm
        providerId={currentUser.id}
        medicalRole={currentUser.medicalRole}
        diagnoses={diagnosisData.data}
        claimCorrectionForm={note.form}
        handleDelete={handleOpen}
      />
    </Formik>

  );

};
